function randomFromInterval(min, max) {
  return Math.random() * (max - min) + min;
}

function cubicBezier(P0, P1, P2, P3, t) {
  var x = (1 - t) ** 3 * P0[0] + 3 * (1 - t) ** 2 * t * P1[0] + 3 * (1 - t) * t ** 2 * P2[0] + t ** 3 * P3[0];
  var y = (1 - t) ** 3 * P0[1] + 3 * (1 - t) ** 2 * t * P1[1] + 3 * (1 - t) * t ** 2 * P2[1] + t ** 3 * P3[1];
  return [x, y];
}

// Happy dog mouth (smile from nose)
export function generateHappyMouth(noseBottomY) {
  let mouthWidth = randomFromInterval(20, 30);
  let mouthDepth = randomFromInterval(8, 15);

  let startY = noseBottomY + randomFromInterval(2, 5);

  // Draw smile line from nose center
  let mouthPoints = [];

  // Left side of smile
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [0, startY],
      [-mouthWidth * 0.3, startY + mouthDepth * 0.3],
      [-mouthWidth * 0.7, startY + mouthDepth * 0.7],
      [-mouthWidth, startY + mouthDepth],
      t
    );
    mouthPoints.push(point);
  }

  // Right side of smile
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [0, startY],
      [mouthWidth * 0.3, startY + mouthDepth * 0.3],
      [mouthWidth * 0.7, startY + mouthDepth * 0.7],
      [mouthWidth, startY + mouthDepth],
      t
    );
    mouthPoints.push(point);
  }

  return mouthPoints;
}

// Panting mouth with tongue
export function generatePantingMouth(noseBottomY) {
  let mouthWidth = randomFromInterval(12, 18);
  let mouthHeight = randomFromInterval(12, 20);

  let startY = noseBottomY + randomFromInterval(2, 5);

  let mouthPoints = [];

  // Left side of open mouth
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [-mouthWidth * 0.8, startY],  // Top left
      [-mouthWidth, startY + mouthHeight * 0.3],
      [-mouthWidth * 0.7, startY + mouthHeight * 0.7],
      [-mouthWidth * 0.3, startY + mouthHeight],  // Bottom left
      t
    );
    mouthPoints.push(point);
  }

  // Bottom of mouth (where tongue connects)
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [-mouthWidth * 0.3, startY + mouthHeight],
      [-mouthWidth * 0.1, startY + mouthHeight * 1.05],
      [mouthWidth * 0.1, startY + mouthHeight * 1.05],
      [mouthWidth * 0.3, startY + mouthHeight],
      t
    );
    mouthPoints.push(point);
  }

  // Right side of open mouth
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [mouthWidth * 0.3, startY + mouthHeight],  // Bottom right
      [mouthWidth * 0.7, startY + mouthHeight * 0.7],
      [mouthWidth, startY + mouthHeight * 0.3],
      [mouthWidth * 0.8, startY],  // Top right
      t
    );
    mouthPoints.push(point);
  }

  // Top of mouth
  for (let t = 0; t <= 1; t += 0.05) {
    let point = cubicBezier(
      [mouthWidth * 0.8, startY],
      [mouthWidth * 0.4, startY - mouthHeight * 0.05],
      [-mouthWidth * 0.4, startY - mouthHeight * 0.05],
      [-mouthWidth * 0.8, startY],
      t
    );
    mouthPoints.push(point);
  }

  return { mouth: mouthPoints, hasTongue: true };
}

// Simple closed mouth
export function generateClosedMouth(noseBottomY) {
  let mouthWidth = randomFromInterval(8, 15);
  let startY = noseBottomY + randomFromInterval(2, 5);

  let mouthPoints = [
    [-mouthWidth, startY],
    [mouthWidth, startY]
  ];

  return mouthPoints;
}

export function generateTongue(mouthBottomY, mouthWidth) {
  let tongueWidth = randomFromInterval(6, 9);
  let tongueLength = randomFromInterval(15, 25);

  // Tongue starts from inside the mouth opening
  // Make sure tongue width is smaller than mouth opening
  let maxTongueWidth = Math.min(tongueWidth, mouthWidth * 0.25);

  let startY = mouthBottomY;

  let tonguePoints = [];

  // Simple tongue shape - narrower at top, wider in middle
  // Left side
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [-maxTongueWidth * 0.3, startY],  // Top left (narrow)
      [-maxTongueWidth * 1.2, startY + tongueLength * 0.4],  // Control point (wider)
      [-maxTongueWidth * 0.7, startY + tongueLength * 0.8],  // Control point (narrower)
      [0, startY + tongueLength],  // Tip (center)
      t
    );
    tonguePoints.push(point);
  }

  // Right side (going back up)
  for (let t = 0; t <= 1; t += 0.1) {
    let point = cubicBezier(
      [0, startY + tongueLength],  // Tip (center)
      [maxTongueWidth * 0.7, startY + tongueLength * 0.8],  // Control point (narrower)
      [maxTongueWidth * 1.2, startY + tongueLength * 0.4],  // Control point (wider)
      [maxTongueWidth * 0.3, startY],  // Top right (narrow)
      t
    );
    tonguePoints.push(point);
  }

  return tonguePoints;
}

export function generateMouth(noseBottomY) {
  // Continuous parameters for mouth generation (no discrete types)
  let mouthOpenness = randomFromInterval(0, 1); // 0 = closed, 1 = wide open
  let mouthWidth = randomFromInterval(8, 30);
  let mouthCurve = randomFromInterval(0, 1); // 0 = straight, 1 = curved smile

  let startY = noseBottomY + randomFromInterval(2, 5);
  let mouthPoints = [];

  if (mouthOpenness < 0.35) {
    // Closed or slight smile
    let curveDepth = mouthCurve * randomFromInterval(3, 12);

    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [-mouthWidth, startY],
        [-mouthWidth * 0.5, startY + curveDepth * 0.5],
        [mouthWidth * 0.5, startY + curveDepth * 0.5],
        [mouthWidth, startY],
        t
      );
      mouthPoints.push(point);
    }

    return { points: mouthPoints, hasTongue: false };
  } else if (mouthOpenness > 0.65) {
    // Wide open with tongue
    let mouthHeight = randomFromInterval(12, 20);

    // Open mouth shape
    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [-mouthWidth * 0.8, startY],
        [-mouthWidth, startY + mouthHeight * 0.3],
        [-mouthWidth * 0.7, startY + mouthHeight * 0.7],
        [-mouthWidth * 0.3, startY + mouthHeight],
        t
      );
      mouthPoints.push(point);
    }

    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [-mouthWidth * 0.3, startY + mouthHeight],
        [-mouthWidth * 0.1, startY + mouthHeight * 1.05],
        [mouthWidth * 0.1, startY + mouthHeight * 1.05],
        [mouthWidth * 0.3, startY + mouthHeight],
        t
      );
      mouthPoints.push(point);
    }

    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [mouthWidth * 0.3, startY + mouthHeight],
        [mouthWidth * 0.7, startY + mouthHeight * 0.7],
        [mouthWidth, startY + mouthHeight * 0.3],
        [mouthWidth * 0.8, startY],
        t
      );
      mouthPoints.push(point);
    }

    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [mouthWidth * 0.8, startY],
        [mouthWidth * 0.4, startY - mouthHeight * 0.05],
        [-mouthWidth * 0.4, startY - mouthHeight * 0.05],
        [-mouthWidth * 0.8, startY],
        t
      );
      mouthPoints.push(point);
    }

    let mouthBottomY = Math.max(...mouthPoints.map(p => p[1]));
    return {
      points: mouthPoints,
      hasTongue: true,
      tongue: generateTongue(mouthBottomY, mouthWidth)
    };
  } else {
    // Moderate smile
    let smileDepth = randomFromInterval(8, 15) * mouthCurve;

    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [0, startY],
        [-mouthWidth * 0.3, startY + smileDepth * 0.3],
        [-mouthWidth * 0.7, startY + smileDepth * 0.7],
        [-mouthWidth, startY + smileDepth],
        t
      );
      mouthPoints.push(point);
    }

    for (let t = 0; t <= 1; t += 0.05) {
      let point = cubicBezier(
        [0, startY],
        [mouthWidth * 0.3, startY + smileDepth * 0.3],
        [mouthWidth * 0.7, startY + smileDepth * 0.7],
        [mouthWidth, startY + smileDepth],
        t
      );
      mouthPoints.push(point);
    }

    return { points: mouthPoints, hasTongue: false };
  }
}
